/* eslint-disable arrow-parens */
// The visual section-tree builder for a card variant's front/back —
// SectionListEditor is the recursive entry point (two_column nests it again
// for its left/right sub-lists), SectionEditor dispatches to one field
// editor per of the 9 section types, matching HealthCardSection in
// healthCardConfig.types.ts exactly.
import { DragEvent, useState } from "react";
import {
	Button,
	Chip,
	IconButton,
	MenuItem,
	Paper,
	Popover,
	Select,
	TextField,
	Checkbox,
	FormControlLabel,
	Typography
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import ClearIcon from "@material-ui/icons/Clear";
import FormatColorTextIcon from "@material-ui/icons/FormatColorText";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import AssetKeyPicker from "./AssetKeyPicker";
import {
	HealthCardAssetKeysResult,
	HealthCardColumn,
	HealthCardKeyValueRow,
	HealthCardLogoRowItem,
	HealthCardSection,
	HealthCardSectionLogo,
	HealthCardStyledText,
	HealthCardTextLine,
	HealthCardTextRun,
	HealthCardTitleLine,
	HealthCardTitleWeight
} from "../../../../@types/healthCardConfig.types";

// Custom drag payload type carrying the dragging section's owning list path
// (e.g. "front" or "front.0.left") — lets a drop target reject a drag that
// started in a different SectionListEditor instance (a nested two_column
// side, or a sibling list entirely) instead of misapplying a foreign index.
const DRAG_LIST_PATH_MIME = "application/x-hc-list-path";

const TITLE_WEIGHT_OPTIONS: { value: HealthCardTitleWeight; label: string }[] = [
	{ value: "normal", label: "Normal" },
	{ value: "bold", label: "Bold" },
	{ value: "extra_bold", label: "Extra Bold" }
];

// A text_block's own default is 11px, a header title's is 13px, and a
// subtitle's is 10px (see .hc-text-block/.hc-title/.hc-subtitle in
// buildHealthCardHtml.service.ts) — all three are included here so each
// field's "no override" state has an explicit, correct-looking option
// rather than a blank/omitted value or the wrong number showing selected.
const TEXT_RUN_SIZE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20];
const TEXT_RUN_DEFAULT_SIZE = 11;

const SECTION_TYPES: HealthCardSection["type"][] = [
	"header",
	"divider",
	"key_value_rows",
	"qr_block",
	"columns",
	"table",
	"label_value_row",
	"text_block",
	"logo_row",
	"two_column"
];

// Sensible starting shape for each type when a new section is added —
// author fills in the specifics from there.
const defaultSectionFor = (type: HealthCardSection["type"]): HealthCardSection => {
	switch (type) {
		case "header":
			return { type, logos: [], title_lines: [""], subtitle_lines: [] };
		case "divider":
			return { type };
		case "key_value_rows":
			return { type, rows: [{ label: "", value_source: "static", static_value: "" }] };
		case "qr_block":
			return { type, caption: "", link_kind: "eligibility_check" };
		case "columns":
			return { type, columns: [{ lines: [""] }, { lines: [""] }] };
		case "table":
			return { type, table: { headers: [], rows: [[""]] } };
		case "label_value_row":
			return { type, label: "", value: "" };
		case "text_block":
			return { type, lines: [""] };
		case "logo_row":
			return { type, logos: [{ asset_key: "" }] };
		case "two_column":
			return { type, left: [], right: [] };
		default:
			return { type: "divider" };
	}
};

// A line can be a plain string (the common case) or an array of runs, each
// with its own weight/color (BBI's "Precertification is required: <bold>
// ..." line) — normalized to a run array here so the editor below always
// works with the richer shape; a plain string becomes a single unstyled run.
const resolveTextRuns = (line: HealthCardTextLine): HealthCardTextRun[] =>
	typeof line === "string" ? [{ text: line }] : line;

const resolveStyledText = (
	value: HealthCardStyledText
): { text: string; weight?: HealthCardTitleWeight; color?: string; size?: number; underline?: boolean } =>
	typeof value === "string" ? { text: value } : value;

// Single icon button that opens a small popover with the weight/size/color
// controls, instead of laying all three out inline — keeps every field this
// attaches to down to one text input + one icon in its resting state. The
// icon turns blue whenever any of the three is actually overridden, so a
// styled field is still visible at a glance without opening the popover.
// `defaultWeight`/`defaultSize` are what the popover's Selects show when
// unset — each call site passes its own field's real CSS default (e.g. a
// logo caption is bold/8px by default, a column line is normal/10px) so
// the dropdown never shows a value that doesn't match what's rendering.
function TextStyleButton({
	weight,
	size,
	color,
	underline,
	defaultWeight = "normal",
	defaultSize = TEXT_RUN_DEFAULT_SIZE,
	onWeightChange,
	onSizeChange,
	onColorChange,
	onUnderlineChange
}: {
	weight?: HealthCardTitleWeight;
	size?: number;
	color?: string;
	underline?: boolean;
	defaultWeight?: HealthCardTitleWeight;
	defaultSize?: number;
	onWeightChange: (weight: HealthCardTitleWeight) => void;
	onSizeChange: (size: number) => void;
	onColorChange: (color: string | undefined) => void;
	onUnderlineChange?: (underline: boolean) => void;
}) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const hasOverride = weight !== undefined || size !== undefined || !!color || !!underline;
	return (
		<>
			<IconButton
				size="small"
				onClick={(event) => setAnchorEl(event.currentTarget)}
				aria-label="Text style (weight, size, color, underline)"
				title="Text style (weight, size, color, underline)"
				style={hasOverride ? { color: "#1976d2" } : undefined}
			>
				<FormatColorTextIcon fontSize="small" />
			</IconButton>
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 4, padding: 8 }}>
					<Select
						value={weight ?? defaultWeight}
						onChange={(event) => onWeightChange(event.target.value as HealthCardTitleWeight)}
						variant="outlined"
						margin="dense"
					>
						{TITLE_WEIGHT_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</Select>
					<Select
						value={size ?? defaultSize}
						onChange={(event) => onSizeChange(Number(event.target.value))}
						variant="outlined"
						margin="dense"
					>
						{TEXT_RUN_SIZE_OPTIONS.map((option) => (
							<MenuItem key={option} value={option}>
								{option}px
							</MenuItem>
						))}
					</Select>
					<TextField
						type="color"
						value={color ?? "#000000"}
						onChange={(event) => onColorChange(event.target.value)}
						variant="outlined"
						margin="dense"
						style={{ width: 64 }}
						inputProps={{ style: { height: 32, padding: 4, cursor: "pointer" } }}
					/>
					<IconButton
						size="small"
						onClick={() => onColorChange(undefined)}
						disabled={!color}
						aria-label="Use default color"
						title="Use default color"
					>
						<ClearIcon fontSize="small" />
					</IconButton>
					{onUnderlineChange && (
						<IconButton
							size="small"
							onClick={() => onUnderlineChange(!underline)}
							aria-label="Toggle underline"
							title="Toggle underline"
							style={underline ? { color: "#1976d2" } : undefined}
						>
							<FormatUnderlinedIcon fontSize="small" />
						</IconButton>
					)}
				</div>
			</Popover>
		</>
	);
}

// Compact single-field editor for a HealthCardStyledText value — a text
// input plus one TextStyleButton, so it takes no more room than a plain
// text field until its style popover is opened. Used wherever a single
// piece of authored text (a caption, a table cell, a label_value_row's
// label/value, a column heading/line, ...) can carry its own style
// override — as opposed to TextRunEditor, which edits one run within a
// text_block's multi-run line.
function StyledTextField({
	value,
	onChange,
	label,
	defaultWeight = "normal",
	defaultSize = TEXT_RUN_DEFAULT_SIZE
}: {
	value: HealthCardStyledText;
	onChange: (next: HealthCardStyledText) => void;
	label?: string;
	defaultWeight?: HealthCardTitleWeight;
	defaultSize?: number;
}) {
	const resolved = resolveStyledText(value);
	const update = (
		patch: Partial<{
			text: string;
			weight: HealthCardTitleWeight;
			color: string | undefined;
			size: number;
			underline: boolean;
		}>
	) => onChange({ ...resolved, ...patch });
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}>
			<TextField
				label={label}
				value={resolved.text}
				onChange={(event) => update({ text: event.target.value })}
				variant="outlined"
				margin="dense"
				// flex:1 (not the `fullWidth` prop) so this field shares its row
				// with the TextStyleButton icon instead of claiming the whole
				// row's width and forcing the icon onto its own line below —
				// `fullWidth` sets width:100%, which a flex-wrap row honors by
				// wrapping the sibling rather than letting this field shrink.
				style={{ flex: 1, minWidth: 120 }}
			/>
			<TextStyleButton
				weight={resolved.weight}
				size={resolved.size}
				color={resolved.color}
				underline={resolved.underline}
				defaultWeight={defaultWeight}
				defaultSize={defaultSize}
				onWeightChange={(weight) => update({ weight })}
				onSizeChange={(size) => update({ size })}
				onColorChange={(color) => update({ color })}
				onUnderlineChange={(underline) => update({ underline })}
			/>
		</div>
	);
}

// Like StringListEditor, but each line is a HealthCardStyledText rather
// than a plain string — used for a column's lines and a table's headers,
// where every entry can carry its own weight/color/size.
function StyledTextListEditor({
	label,
	values,
	onChange,
	defaultWeight,
	defaultSize
}: {
	label: string;
	values: HealthCardStyledText[];
	onChange: (next: HealthCardStyledText[]) => void;
	defaultWeight?: HealthCardTitleWeight;
	defaultSize?: number;
}) {
	const update = (index: number, next: HealthCardStyledText) =>
		onChange(values.map((value, i) => (i === index ? next : value)));
	const remove = (index: number) => onChange(values.filter((_, i) => i !== index));
	const add = () => onChange([...values, ""]);

	return (
		<div style={{ marginBottom: 8 }}>
			{values.map((value, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<div key={index} style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
					<div style={{ flex: 1, minWidth: 120 }}>
						<StyledTextField
							label={values.length > 1 ? `${label} ${index + 1}` : label}
							value={value}
							onChange={(next) => update(index, next)}
							defaultWeight={defaultWeight}
							defaultSize={defaultSize}
						/>
					</div>
					<IconButton size="small" onClick={() => remove(index)} aria-label={`Remove ${label}`}>
						<DeleteIcon fontSize="small" />
					</IconButton>
				</div>
			))}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={add}>
				Add {label}
			</Button>
		</div>
	);
}

function TextRunEditor({
	run,
	onChange,
	onRemove
}: {
	run: HealthCardTextRun;
	onChange: (next: HealthCardTextRun) => void;
	onRemove: () => void;
}) {
	// `bold` is the legacy flag — folded into `weight` here so TextStyleButton
	// sees one real value (and shows the icon as overridden) instead of two
	// separate, possibly-conflicting fields; onWeightChange below normalizes
	// any edit onto `weight` going forward and drops the legacy flag.
	const weight = run.weight ?? (run.bold ? "bold" : undefined);
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}>
			<TextField
				value={run.text}
				onChange={(event) => onChange({ ...run, text: event.target.value })}
				variant="outlined"
				margin="dense"
				style={{ flex: 1, minWidth: 120 }}
			/>
			<TextStyleButton
				weight={weight}
				size={run.size}
				color={run.color}
				underline={run.underline}
				onWeightChange={(nextWeight) => onChange({ ...run, weight: nextWeight, bold: undefined })}
				onSizeChange={(size) => onChange({ ...run, size })}
				onColorChange={(color) => onChange({ ...run, color })}
				onUnderlineChange={(underline) => onChange({ ...run, underline })}
			/>
			<IconButton size="small" onClick={onRemove} aria-label="Remove run">
				<DeleteIcon fontSize="small" />
			</IconButton>
		</div>
	);
}

// One line of a text_block, editable as one or more runs — most lines have
// exactly one run, but a line can mix differently-weighted/colored runs
// (e.g. a bold lead-in followed by normal continuation on the same line).
function TextLineListEditor({
	lines,
	onChange
}: {
	lines: HealthCardTextLine[];
	onChange: (next: HealthCardTextLine[]) => void;
}) {
	const updateLine = (index: number, runs: HealthCardTextRun[]) =>
		onChange(lines.map((line, i) => (i === index ? runs : line)));
	const removeLine = (index: number) => onChange(lines.filter((_, i) => i !== index));
	const addLine = () => onChange([...lines, [{ text: "" }]]);

	return (
		<div style={{ marginBottom: 8 }}>
			{lines.map((line, index) => {
				const runs = resolveTextRuns(line);
				const updateRun = (runIndex: number, next: HealthCardTextRun) =>
					updateLine(
						index,
						runs.map((run, i) => (i === runIndex ? next : run))
					);
				const removeRun = (runIndex: number) =>
					updateLine(
						index,
						runs.filter((_, i) => i !== runIndex)
					);
				const addRun = () => updateLine(index, [...runs, { text: "" }]);
				return (
					// eslint-disable-next-line react/no-array-index-key
					<Paper key={index} variant="outlined" style={{ padding: 8, marginBottom: 8 }}>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: 4
							}}
						>
							<Typography variant="caption" color="textSecondary">
								Line {index + 1}
							</Typography>
							<IconButton size="small" onClick={() => removeLine(index)} aria-label="Remove line">
								<DeleteIcon fontSize="small" />
							</IconButton>
						</div>
						{runs.map((run, runIndex) => (
							<TextRunEditor
								// eslint-disable-next-line react/no-array-index-key
								key={runIndex}
								run={run}
								onChange={(next) => updateRun(runIndex, next)}
								onRemove={() => removeRun(runIndex)}
							/>
						))}
						<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addRun}>
							Add Text
						</Button>
					</Paper>
				);
			})}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addLine}>
				Add Line
			</Button>
		</div>
	);
}

const resolveTitleLine = (
	line: HealthCardTitleLine
): { text: string; weight: HealthCardTitleWeight; color?: string; size?: number; underline?: boolean } =>
	typeof line === "string"
		? { text: line, weight: "bold" }
		: {
				text: line.text,
				weight: line.weight ?? "bold",
				color: line.color,
				size: line.size,
				underline: line.underline
		  };

// Editor for a header's title_lines/subtitle_lines — each line gets its own
// text field, a font-weight dropdown (normal/bold/extra bold), a font-size
// dropdown, and a color swatch, so e.g. a group name can sit in a lighter
// weight/smaller size above a heavier, differently-colored plan-name line.
// `defaultSize` is what an unset line's size Select shows selected — the
// title/subtitle CSS defaults differ (13px/10px), so the caller passes its
// own rather than this component guessing which field it's editing.
function TitleLineListEditor({
	label,
	values,
	onChange,
	defaultSize
}: {
	label: string;
	values: HealthCardTitleLine[];
	onChange: (next: HealthCardTitleLine[]) => void;
	defaultSize: number;
}) {
	const update = (
		index: number,
		patch: Partial<{
			text: string;
			weight: HealthCardTitleWeight;
			color?: string;
			size?: number;
			underline?: boolean;
		}>
	) => {
		const next = values.map((line, i) => {
			if (i !== index) return line;
			const resolved = resolveTitleLine(line);
			return { ...resolved, ...patch };
		});
		onChange(next);
	};
	const remove = (index: number) => onChange(values.filter((_, i) => i !== index));
	const add = () => onChange([...values, { text: "", weight: "bold" }]);

	return (
		<div style={{ marginBottom: 8 }}>
			{values.map((line, index) => {
				const { text } = resolveTitleLine(line);
				// Raw (possibly-unset) fields, as opposed to resolveTitleLine's
				// always-defined `weight` (defaults to "bold") — TextStyleButton
				// needs to know whether this line actually overrides anything,
				// not what it defaults to, or its icon would show every plain
				// line as "styled" just because bold happens to be the default.
				const raw: { weight?: HealthCardTitleWeight; color?: string; size?: number; underline?: boolean } =
					typeof line === "string" ? {} : line;
				return (
					// eslint-disable-next-line react/no-array-index-key
					<div
						key={index}
						style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}
					>
						<TextField
							label={values.length > 1 ? `${label} ${index + 1}` : label}
							value={text}
							onChange={(event) => update(index, { text: event.target.value })}
							variant="outlined"
							margin="dense"
							style={{ flex: 1, minWidth: 120 }}
						/>
						<TextStyleButton
							weight={raw.weight}
							size={raw.size}
							color={raw.color}
							underline={raw.underline}
							defaultWeight="bold"
							defaultSize={defaultSize}
							onWeightChange={(weight) => update(index, { weight })}
							onSizeChange={(size) => update(index, { size })}
							onColorChange={(color) => update(index, { color })}
							onUnderlineChange={(underline) => update(index, { underline })}
						/>
						<IconButton size="small" onClick={() => remove(index)} aria-label={`Remove ${label}`}>
							<DeleteIcon fontSize="small" />
						</IconButton>
					</div>
				);
			})}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={add}>
				Add {label}
			</Button>
		</div>
	);
}

function LogoListEditor({
	logos,
	onChange,
	assetKeys,
	withAlign
}: {
	logos: HealthCardSectionLogo[];
	onChange: (next: HealthCardSectionLogo[]) => void;
	assetKeys?: HealthCardAssetKeysResult;
	withAlign: boolean;
}) {
	const update = (index: number, patch: Partial<HealthCardSectionLogo>) => {
		const next = logos.map((logo, i) => (i === index ? { ...logo, ...patch } : logo));
		onChange(next);
	};
	const remove = (index: number) => onChange(logos.filter((_, i) => i !== index));
	const add = () => onChange([...logos, { asset_key: "", align: "left" }]);

	return (
		<div style={{ marginBottom: 8 }}>
			{logos.map((logo, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<div
					key={index}
					style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}
				>
					<div style={{ flex: 2 }}>
						<AssetKeyPicker
							assetKeys={assetKeys}
							value={logo.asset_key}
							onChange={(assetKey) => update(index, { asset_key: assetKey })}
						/>
					</div>
					{withAlign && (
						<Select
							value={logo.align}
							onChange={(event) =>
								update(index, { align: event.target.value as HealthCardSectionLogo["align"] })
							}
							variant="outlined"
							margin="dense"
						>
							<MenuItem value="left">Left</MenuItem>
							<MenuItem value="center">Center</MenuItem>
							<MenuItem value="right">Right</MenuItem>
						</Select>
					)}
					<div style={{ flex: 2 }}>
						<StyledTextField
							label="Caption"
							value={logo.caption ?? ""}
							onChange={(nextCaption) => {
								const resolvedNext = resolveStyledText(nextCaption);
								update(index, { caption: resolvedNext.text ? nextCaption : undefined });
							}}
							defaultWeight="bold"
							defaultSize={8}
						/>
					</div>
					<IconButton size="small" onClick={() => remove(index)} aria-label="Remove logo">
						<DeleteIcon fontSize="small" />
					</IconButton>
				</div>
			))}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={add}>
				Add Logo
			</Button>
		</div>
	);
}

function HeaderFields({
	section,
	onChange,
	assetKeys
}: {
	section: Extract<HealthCardSection, { type: "header" }>;
	onChange: (next: Extract<HealthCardSection, { type: "header" }>) => void;
	assetKeys?: HealthCardAssetKeysResult;
}) {
	return (
		<>
			<LogoListEditor
				logos={section.logos}
				onChange={(logos) => onChange({ ...section, logos })}
				assetKeys={assetKeys}
				withAlign
			/>
			<TitleLineListEditor
				label="Title line"
				values={section.title_lines}
				onChange={(title_lines) => onChange({ ...section, title_lines })}
				defaultSize={13}
			/>
			<TitleLineListEditor
				label="Subtitle line"
				values={section.subtitle_lines}
				onChange={(subtitle_lines) => onChange({ ...section, subtitle_lines })}
				defaultSize={10}
			/>
			<Select
				value={section.layout ?? "centered"}
				onChange={(event) => onChange({ ...section, layout: event.target.value as "centered" | "left" })}
				variant="outlined"
				margin="dense"
				fullWidth
			>
				<MenuItem value="centered">Centered layout</MenuItem>
				<MenuItem value="left">Left-aligned layout (Clinic Card style)</MenuItem>
			</Select>
		</>
	);
}

function KeyValueRowsFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "key_value_rows" }>;
	onChange: (next: Extract<HealthCardSection, { type: "key_value_rows" }>) => void;
}) {
	const update = (index: number, patch: Partial<HealthCardKeyValueRow>) => {
		const rows = section.rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
		onChange({ ...section, rows });
	};
	const remove = (index: number) => onChange({ ...section, rows: section.rows.filter((_, i) => i !== index) });
	const add = () =>
		onChange({ ...section, rows: [...section.rows, { label: "", value_source: "static", static_value: "" }] });

	return (
		<div>
			{section.rows.map((row, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<div
					key={index}
					style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}
				>
					<TextField
						label="Label"
						value={row.label}
						onChange={(event) => update(index, { label: event.target.value })}
						variant="outlined"
						margin="dense"
						style={{ flex: 1, minWidth: 120 }}
					/>
					<Select
						value={row.value_source}
						onChange={(event) =>
							update(index, {
								value_source: event.target.value as HealthCardKeyValueRow["value_source"]
							})
						}
						variant="outlined"
						margin="dense"
					>
						<MenuItem value="static">Static text</MenuItem>
						<MenuItem value="employee_id">Employee ID</MenuItem>
						<MenuItem value="employee_name">Employee Name</MenuItem>
					</Select>
					{row.value_source === "static" && (
						<TextField
							label="Value"
							value={row.static_value ?? ""}
							onChange={(event) => update(index, { static_value: event.target.value })}
							variant="outlined"
							margin="dense"
							style={{ flex: 1, minWidth: 120 }}
						/>
					)}
					{row.value_source === "employee_id" && (
						<Select
							value={row.mask ?? ""}
							onChange={(event) =>
								update(index, {
									mask: (event.target.value || undefined) as HealthCardKeyValueRow["mask"]
								})
							}
							displayEmpty
							variant="outlined"
							margin="dense"
						>
							<MenuItem value="">No masking</MenuItem>
							<MenuItem value="ssn_last4">Mask as XXX-XX-1234</MenuItem>
						</Select>
					)}
					<TextStyleButton
						weight={row.weight}
						size={row.size}
						color={row.color}
						underline={row.underline}
						defaultWeight="bold"
						defaultSize={13}
						onWeightChange={(weight) => update(index, { weight })}
						onSizeChange={(size) => update(index, { size })}
						onColorChange={(color) => update(index, { color })}
						onUnderlineChange={(underline) => update(index, { underline })}
					/>
					<IconButton size="small" onClick={() => remove(index)} aria-label="Remove row">
						<DeleteIcon fontSize="small" />
					</IconButton>
				</div>
			))}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={add}>
				Add Row
			</Button>
		</div>
	);
}

function QrBlockFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "qr_block" }>;
	onChange: (next: Extract<HealthCardSection, { type: "qr_block" }>) => void;
}) {
	return (
		<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
			<div style={{ flex: 1, minWidth: 120 }}>
				<StyledTextField
					label="Caption"
					value={section.caption ?? ""}
					onChange={(nextCaption) => {
						const resolvedNext = resolveStyledText(nextCaption);
						onChange({ ...section, caption: resolvedNext.text ? nextCaption : undefined });
					}}
					defaultWeight="bold"
					defaultSize={10}
				/>
			</div>
			<Select
				value={section.align ?? "block"}
				onChange={(event) => onChange({ ...section, align: event.target.value as "block" | "left" | "right" })}
				variant="outlined"
				margin="dense"
			>
				<MenuItem value="block">Own row</MenuItem>
				<MenuItem value="left">Float left</MenuItem>
				<MenuItem value="right">Float right</MenuItem>
			</Select>
		</div>
	);
}

function ColumnsFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "columns" }>;
	onChange: (next: Extract<HealthCardSection, { type: "columns" }>) => void;
}) {
	const update = (index: number, patch: Partial<HealthCardColumn>) => {
		const columns = section.columns.map((col, i) => (i === index ? { ...col, ...patch } : col));
		onChange({ ...section, columns });
	};
	const remove = (index: number) => onChange({ ...section, columns: section.columns.filter((_, i) => i !== index) });
	const add = () => onChange({ ...section, columns: [...section.columns, { lines: [""] }] });

	return (
		<div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
			{section.columns.map((column, index) => (
				// eslint-disable-next-line react/no-array-index-key
				<Paper key={index} variant="outlined" style={{ padding: 8, flex: 1, minWidth: 180 }}>
					<div style={{ display: "flex", justifyContent: "space-between" }}>
						<span style={{ fontSize: 11, color: "#888" }}>Column {index + 1}</span>
						<IconButton size="small" onClick={() => remove(index)} aria-label="Remove column">
							<DeleteIcon fontSize="small" />
						</IconButton>
					</div>
					<StyledTextField
						label="Heading (optional)"
						value={column.heading ?? ""}
						onChange={(nextHeading) => {
							const resolvedNext = resolveStyledText(nextHeading);
							update(index, { heading: resolvedNext.text ? nextHeading : undefined });
						}}
						defaultWeight="bold"
						defaultSize={10}
					/>
					<StyledTextListEditor
						label="Line"
						values={column.lines}
						onChange={(lines) => update(index, { lines })}
						defaultSize={10}
					/>
				</Paper>
			))}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={add}>
				Add Column
			</Button>
		</div>
	);
}

function TableFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "table" }>;
	onChange: (next: Extract<HealthCardSection, { type: "table" }>) => void;
}) {
	const { table } = section;
	const setTable = (patch: Partial<typeof table>) => onChange({ ...section, table: { ...table, ...patch } });

	const updateCell = (rowIndex: number, cellIndex: number, value: HealthCardStyledText) => {
		const rows = table.rows.map((row, i) =>
			i === rowIndex ? row.map((cell, j) => (j === cellIndex ? value : cell)) : row
		);
		setTable({ rows });
	};
	const removeRow = (rowIndex: number) => setTable({ rows: table.rows.filter((_, i) => i !== rowIndex) });
	const addRow = () => setTable({ rows: [...table.rows, table.headers.map(() => "")] });
	const addCellToRow = (rowIndex: number) =>
		setTable({ rows: table.rows.map((row, i) => (i === rowIndex ? [...row, ""] : row)) });
	const removeCellFromRow = (rowIndex: number, cellIndex: number) =>
		setTable({ rows: table.rows.map((row, i) => (i === rowIndex ? row.filter((_, j) => j !== cellIndex) : row)) });

	return (
		<div>
			<StyledTextListEditor
				label="Header"
				values={table.headers}
				onChange={(headers) => setTable({ headers })}
				defaultWeight="bold"
				defaultSize={10}
			/>
			<div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Rows (label + one value per header)</div>
			{table.rows.map((row, rowIndex) => (
				// eslint-disable-next-line react/no-array-index-key
				<div
					key={rowIndex}
					style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4, flexWrap: "wrap" }}
				>
					{row.map((cell, cellIndex) => (
						// eslint-disable-next-line react/no-array-index-key
						<div key={cellIndex} style={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
							<div style={{ flex: 1, minWidth: 120 }}>
								<StyledTextField
									value={cell}
									onChange={(next) => updateCell(rowIndex, cellIndex, next)}
									defaultWeight="bold"
									defaultSize={10}
								/>
							</div>
							<IconButton
								size="small"
								onClick={() => removeCellFromRow(rowIndex, cellIndex)}
								aria-label="Remove cell"
							>
								<ClearIcon fontSize="small" />
							</IconButton>
						</div>
					))}
					<Button
						size="small"
						startIcon={<AddIcon fontSize="small" />}
						onClick={() => addCellToRow(rowIndex)}
					>
						Add Cell
					</Button>
					<IconButton size="small" onClick={() => removeRow(rowIndex)} aria-label="Remove row">
						<DeleteIcon fontSize="small" />
					</IconButton>
				</div>
			))}
			<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addRow}>
				Add Row
			</Button>
		</div>
	);
}

function LabelValueRowFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "label_value_row" }>;
	onChange: (next: Extract<HealthCardSection, { type: "label_value_row" }>) => void;
}) {
	return (
		<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
			<div style={{ flex: 1, minWidth: 120 }}>
				<StyledTextField
					label="Label"
					value={section.label}
					onChange={(label) => onChange({ ...section, label })}
					defaultSize={12}
				/>
			</div>
			<div style={{ flex: 1, minWidth: 120 }}>
				<StyledTextField
					label="Value"
					value={section.value}
					onChange={(value) => onChange({ ...section, value })}
					defaultSize={12}
				/>
			</div>
		</div>
	);
}

function TextBlockFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "text_block" }>;
	onChange: (next: Extract<HealthCardSection, { type: "text_block" }>) => void;
}) {
	return (
		<>
			<TextLineListEditor lines={section.lines} onChange={(lines) => onChange({ ...section, lines })} />
			<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
				<Select
					value={section.align ?? "center"}
					onChange={(event) => onChange({ ...section, align: event.target.value as "left" | "center" })}
					variant="outlined"
					margin="dense"
				>
					<MenuItem value="center">Center</MenuItem>
					<MenuItem value="left">Left</MenuItem>
				</Select>
				<FormControlLabel
					control={
						<Checkbox
							checked={!!section.underline}
							onChange={(event) => onChange({ ...section, underline: event.target.checked })}
						/>
					}
					label="Underline"
				/>
			</div>
		</>
	);
}

function LogoRowFields({
	section,
	onChange,
	assetKeys
}: {
	section: Extract<HealthCardSection, { type: "logo_row" }>;
	onChange: (next: Extract<HealthCardSection, { type: "logo_row" }>) => void;
	assetKeys?: HealthCardAssetKeysResult;
}) {
	const asHeaderLogos = section.logos.map((l) => ({ ...l, align: "center" as const }));
	const handleLogosChange = (next: HealthCardSectionLogo[]) =>
		onChange({
			...section,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			logos: next.map(({ align: _align, ...rest }): HealthCardLogoRowItem => rest)
		});

	return (
		<>
			<LogoListEditor
				logos={asHeaderLogos}
				onChange={handleLogosChange}
				assetKeys={assetKeys}
				withAlign={false}
			/>
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
				<Select
					value={section.align ?? "center"}
					onChange={(event) =>
						onChange({ ...section, align: event.target.value as "center" | "right" | "space-between" })
					}
					variant="outlined"
					margin="dense"
				>
					<MenuItem value="center">Center</MenuItem>
					<MenuItem value="right">Right</MenuItem>
					<MenuItem value="space-between">Space Between (span full width)</MenuItem>
				</Select>
				<Select
					value={typeof section.size === "number" ? "custom" : section.size ?? "normal"}
					onChange={(event) => {
						const next = event.target.value as "normal" | "large" | "custom";
						// 60 matches the "Normal" preset's own max-height, so
						// switching to Custom starts from what's already showing
						// rather than jumping to an arbitrary number.
						onChange({ ...section, size: next === "custom" ? 60 : next });
					}}
					variant="outlined"
					margin="dense"
				>
					<MenuItem value="normal">Normal</MenuItem>
					<MenuItem value="large">Large</MenuItem>
					<MenuItem value="custom">Custom</MenuItem>
				</Select>
				{typeof section.size === "number" && (
					<TextField
						label="Max Height (px)"
						type="number"
						value={section.size}
						onChange={(event) => {
							const px = Number(event.target.value);
							if (!Number.isNaN(px)) onChange({ ...section, size: px });
						}}
						variant="outlined"
						margin="dense"
						style={{ width: 130 }}
						inputProps={{ min: 10, max: 300 }}
					/>
				)}
			</div>
		</>
	);
}

function TwoColumnFields({
	section,
	onChange,
	assetKeys,
	path
}: {
	section: Extract<HealthCardSection, { type: "two_column" }>;
	onChange: (next: Extract<HealthCardSection, { type: "two_column" }>) => void;
	assetKeys?: HealthCardAssetKeysResult;
	path: string;
}) {
	return (
		<>
			<TextField
				label="Left column width % (blank = auto)"
				type="number"
				value={section.left_width_percent ?? ""}
				onChange={(event) =>
					onChange({
						...section,
						left_width_percent: event.target.value ? Number(event.target.value) : undefined
					})
				}
				variant="outlined"
				margin="dense"
				fullWidth
				style={{ marginBottom: 8 }}
			/>
			<div style={{ display: "flex", gap: 16 }}>
				<div style={{ flex: 1, border: "1px dashed #ccc", padding: 8 }}>
					<div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Left</div>
					<SectionListEditor
						sections={section.left}
						onChange={(left) => onChange({ ...section, left })}
						assetKeys={assetKeys}
						path={`${path}.left`}
					/>
				</div>
				<div style={{ flex: 1, border: "1px dashed #ccc", padding: 8 }}>
					<div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>Right</div>
					<SectionListEditor
						sections={section.right}
						onChange={(right) => onChange({ ...section, right })}
						assetKeys={assetKeys}
						path={`${path}.right`}
					/>
				</div>
			</div>
		</>
	);
}

function DividerFields({
	section,
	onChange
}: {
	section: Extract<HealthCardSection, { type: "divider" }>;
	onChange: (next: Extract<HealthCardSection, { type: "divider" }>) => void;
}) {
	return (
		<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
			<span style={{ fontSize: 11, color: "#888" }}>A plain horizontal divider line.</span>
			<TextField
				type="color"
				value={section.color ?? "#000000"}
				onChange={(event) => onChange({ ...section, color: event.target.value })}
				variant="outlined"
				margin="dense"
				style={{ width: 64 }}
				inputProps={{ style: { height: 32, padding: 4, cursor: "pointer" } }}
			/>
			<IconButton
				size="small"
				onClick={() => onChange({ ...section, color: undefined })}
				disabled={!section.color}
				aria-label="Use card's default color"
				title="Use card's default color"
			>
				<ClearIcon fontSize="small" />
			</IconButton>
		</div>
	);
}

// `path` (e.g. "front.2" or "back.0.left.1") mirrors the exact same
// front/back-then-array-index addressing buildHealthCardHtml.service.ts
// stamps onto each rendered section's `data-hc-path` attribute — only
// consumed here to relay into TwoColumnFields' nested SectionListEditor
// calls (see click-to-jump wiring in HealthCardConfig.tsx), every other
// field editor ignores it.
function SectionEditor({
	section,
	onChange,
	assetKeys,
	path
}: {
	section: HealthCardSection;
	onChange: (next: HealthCardSection) => void;
	assetKeys?: HealthCardAssetKeysResult;
	path: string;
}) {
	switch (section.type) {
		case "header":
			return <HeaderFields section={section} onChange={onChange} assetKeys={assetKeys} />;
		case "divider":
			return <DividerFields section={section} onChange={onChange} />;
		case "key_value_rows":
			return <KeyValueRowsFields section={section} onChange={onChange} />;
		case "qr_block":
			return <QrBlockFields section={section} onChange={onChange} />;
		case "columns":
			return <ColumnsFields section={section} onChange={onChange} />;
		case "table":
			return <TableFields section={section} onChange={onChange} />;
		case "label_value_row":
			return <LabelValueRowFields section={section} onChange={onChange} />;
		case "text_block":
			return <TextBlockFields section={section} onChange={onChange} />;
		case "logo_row":
			return <LogoRowFields section={section} onChange={onChange} assetKeys={assetKeys} />;
		case "two_column":
			return <TwoColumnFields section={section} onChange={onChange} assetKeys={assetKeys} path={path} />;
		default:
			return null;
	}
}

const SECTION_TYPE_LABELS: Record<HealthCardSection["type"], string> = {
	header: "Header",
	divider: "Divider",
	key_value_rows: "Key/Value Rows",
	qr_block: "QR Code",
	columns: "Columns",
	table: "Table",
	label_value_row: "Label + Value Row",
	text_block: "Text Block",
	logo_row: "Logo Row",
	two_column: "Two Column Layout"
};

interface SectionListEditorProps {
	sections: HealthCardSection[];
	onChange: (next: HealthCardSection[]) => void;
	assetKeys?: HealthCardAssetKeysResult;
	// Matches buildHealthCardHtml.service.ts's data-hc-path addressing
	// ("front", "back.2.left") — stamped onto each section's Paper below so
	// a click in the live preview iframe can scroll to the matching editor
	// block. See HealthCardConfig.tsx's iframe onLoad handler.
	path: string;
}

// Recursive entry point — a two_column section's left/right are each
// rendered by nesting this same component (see TwoColumnFields above).
export function SectionListEditor({ sections, onChange, assetKeys, path }: SectionListEditorProps) {
	const [newSectionType, setNewSectionType] = useState<HealthCardSection["type"]>("text_block");
	// Drag-to-reorder state, scoped to this one SectionListEditor instance —
	// sections can only be dragged within the same front/back (or two_column
	// left/right) list they already belong to, matching moveUp/moveDown's
	// existing scope, not moved across lists.
	const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
	const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
	// Purely local UI state, not part of the saved config — indexed by
	// position rather than a stable section id (sections don't have one),
	// so collapsed-ness stays with a list slot across a reorder rather than
	// following the section's content. Acceptable for a manual toggle like
	// this; matches how data-hc-path already addresses sections by index.
	const [collapsedIndices, setCollapsedIndices] = useState<Set<number>>(new Set());
	const toggleCollapsed = (index: number) =>
		setCollapsedIndices((prev) => {
			const next = new Set(prev);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});

	const updateAt = (index: number, next: HealthCardSection) =>
		onChange(sections.map((section, i) => (i === index ? next : section)));
	const removeAt = (index: number) => onChange(sections.filter((_, i) => i !== index));
	const moveUp = (index: number) => {
		if (index === 0) return;
		const next = [...sections];
		[next[index - 1], next[index]] = [next[index], next[index - 1]];
		onChange(next);
	};
	const moveDown = (index: number) => {
		if (index === sections.length - 1) return;
		const next = [...sections];
		[next[index], next[index + 1]] = [next[index + 1], next[index]];
		onChange(next);
	};
	const moveTo = (from: number, to: number) => {
		if (from === to) return;
		const next = [...sections];
		const [moved] = next.splice(from, 1);
		next.splice(to, 0, moved);
		onChange(next);
	};
	const clearDragState = () => {
		setDraggedIndex(null);
		setDragOverIndex(null);
	};
	const addSection = () => onChange([...sections, defaultSectionFor(newSectionType)]);

	return (
		<div>
			{sections.map((section, index) => {
				const sectionPath = `${path}.${index}`;
				const isDragging = draggedIndex === index;
				const showDropIndicator = draggedIndex !== null && dragOverIndex === index && draggedIndex !== index;
				const isCollapsed = collapsedIndices.has(index);
				return (
					<Paper
						key={index}
						variant="outlined"
						style={{
							padding: 12,
							marginBottom: 8,
							opacity: isDragging ? 0.4 : 1,
							borderTop: showDropIndicator ? "3px solid #1976d2" : undefined
						}}
						data-hc-path={sectionPath}
						onDragOver={(event) => {
							// Always preventDefault (rather than gating on the
							// draggedIndex state) — a same-tick dragstart -> dragover
							// sequence can fire before React flushes the state update,
							// and skipping preventDefault here would tell the browser
							// this isn't a valid drop target, so the drop event would
							// never fire at all.
							event.preventDefault();
							event.dataTransfer.dropEffect = "move";
							if (dragOverIndex !== index) setDragOverIndex(index);
						}}
						onDrop={(event) => {
							event.preventDefault();
							// Only accept a drop that originated from THIS SAME list
							// (matches moveUp/moveDown's scope) — otherwise a section
							// dragged out of a nested two_column side and dropped on
							// an unrelated list would apply its origin-list index to
							// this list's array, silently reordering/corrupting the
							// wrong items.
							if (event.dataTransfer.getData(DRAG_LIST_PATH_MIME) !== path) {
								clearDragState();
								return;
							}
							// Read the source index from dataTransfer rather than the
							// draggedIndex state — a same-tick dragstart -> drop
							// sequence can fire before React flushes the state
							// update, but dataTransfer isn't subject to that timing.
							const sourceIndex = Number(event.dataTransfer.getData("text/plain"));
							if (!Number.isNaN(sourceIndex)) moveTo(sourceIndex, index);
							clearDragState();
						}}
					>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								marginBottom: isCollapsed ? 0 : 8
							}}
						>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<span
									draggable
									onDragStart={(event: DragEvent<HTMLSpanElement>) => {
										setDraggedIndex(index);
										event.dataTransfer.effectAllowed = "move";
										event.dataTransfer.setData("text/plain", String(index));
										event.dataTransfer.setData(DRAG_LIST_PATH_MIME, path);
									}}
									onDragEnd={clearDragState}
									style={{ display: "inline-flex", cursor: "grab" }}
									title="Drag to reorder"
									aria-label="Drag to reorder"
								>
									<DragIndicatorIcon fontSize="small" style={{ color: "#888" }} />
								</span>
								<IconButton
									size="small"
									onClick={() => toggleCollapsed(index)}
									aria-label={isCollapsed ? "Expand section" : "Collapse section"}
									title={isCollapsed ? "Expand" : "Collapse"}
								>
									{isCollapsed ? (
										<ChevronRightIcon fontSize="small" />
									) : (
										<ExpandMoreIcon fontSize="small" />
									)}
								</IconButton>
								<Chip size="small" label={SECTION_TYPE_LABELS[section.type]} />
							</div>
							<div>
								<IconButton
									size="small"
									onClick={() => moveUp(index)}
									disabled={index === 0}
									aria-label="Move up"
								>
									<ArrowUpwardIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									onClick={() => moveDown(index)}
									disabled={index === sections.length - 1}
									aria-label="Move down"
								>
									<ArrowDownwardIcon fontSize="small" />
								</IconButton>
								<IconButton size="small" onClick={() => removeAt(index)} aria-label="Remove section">
									<DeleteIcon fontSize="small" />
								</IconButton>
							</div>
						</div>
						{!isCollapsed && (
							<SectionEditor
								section={section}
								onChange={(next) => updateAt(index, next)}
								assetKeys={assetKeys}
								path={sectionPath}
							/>
						)}
					</Paper>
				);
			})}

			<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
				<Select
					value={newSectionType}
					onChange={(event) => setNewSectionType(event.target.value as HealthCardSection["type"])}
					variant="outlined"
					margin="dense"
				>
					{SECTION_TYPES.map((type) => (
						<MenuItem key={type} value={type}>
							{SECTION_TYPE_LABELS[type]}
						</MenuItem>
					))}
				</Select>
				<Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addSection}>
					Add {SECTION_TYPE_LABELS[newSectionType]}
				</Button>
			</div>
		</div>
	);
}

export default SectionListEditor;
